// src/auth/authProvider.ts
import { AuthProvider } from "react-admin";

import { GRAPHQL_ENDPOINT } from "../config";
import { PHONE_SIGN_IN_PATH } from "../constants/telephony";
import {
  AdminMfaResponse,
  AdminMfaSettings,
  CompanySignInResult,
  CompanySignInStart,
  CompanySignInStatus,
  CompleteAdminMfaInput,
  DisableAdminMfaInput,
  LoginStatus,
  LoginUserResponse,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";
import { AuthMethod, AuthSurface, clearAuthData, getAuthData, storeAuthData } from "../lib/authStorage";
import { captureException } from "../lib/errorHandling";
import { logout } from "../lib/logout";
import { accessibleResources, canAccessResource, getPortalAccess, type PortalAccess } from "./access";
import { getCurrentUserIdentity } from "./lib";
import {
  ADMIN_MFA_SETTINGS,
  BEGIN_ADMIN_MFA_ENROLLMENT,
  BEGIN_COMPANY_SIGN_IN,
  CANCEL_ADMIN_MFA_ENROLLMENT,
  CHANGE_ADMIN_PASSWORD,
  COMPLETE_ADMIN_MFA,
  COMPLETE_COMPANY_SIGN_IN,
  DISABLE_ADMIN_MFA,
  LEARNING_CHANGE_PASSWORD_MUTATION,
  LEARNING_LOGIN_MUTATION,
  LEARNING_REQUEST_PASSWORD_RESET_MUTATION,
  LEARNING_RESET_PASSWORD_MUTATION,
  LOGIN_MUTATION,
} from "./mutations";
import { refreshPortalAccess } from "./refreshPortalAccess";

type GraphQLError = { message: string };

type GraphQLResponse<T> = {
  data?: T;
  errors?: GraphQLError[];
};

type LearningViewer = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  organizationId: string;
};

type LearningAuthPayload = {
  token: string;
  viewer: LearningViewer;
};

type LearningLoginData = { learningLogin?: LearningAuthPayload | null };
type LearningResetPasswordData = { learningResetPassword?: LearningAuthPayload | null };
type LearningChangePasswordData = { learningChangePassword?: LearningAuthPayload | null };
type LearningRequestPasswordResetData = { learningRequestPasswordReset?: boolean | null };
type CompleteAdminMfaData = { completeAdminMfa?: AdminMfaResponse | null };
type ChangeAdminPasswordData = { changeAdminPassword?: boolean | null };
type AdminMfaSettingsData = { adminMfaSettings?: AdminMfaSettings | null };
type BeginAdminMfaEnrollmentData = { beginAdminMfaEnrollment?: AdminMfaResponse | null };
type CancelAdminMfaEnrollmentData = { cancelAdminMfaEnrollment?: boolean | null };
type DisableAdminMfaData = { disableAdminMfa?: AdminMfaResponse | null };
type BeginCompanySignInData = { beginCompanySignIn?: CompanySignInStart | null };
type CompleteCompanySignInData = { completeCompanySignIn?: CompanySignInResult | null };

export class LearningLoginRejectedError extends Error {
  readonly surface: AuthSurface = "learning";
}

const MFA_REQUIRED_ERROR = "mfa-required";

const isMfaRequiredError = (error: unknown): boolean => {
  if (error instanceof Error && error.message === MFA_REQUIRED_ERROR) return true;
  if (typeof error !== "object" || error === null || !("graphQLErrors" in error)) return false;

  const graphQLErrors = error.graphQLErrors;
  return (
    Array.isArray(graphQLErrors) &&
    graphQLErrors.some(
      graphQLError =>
        typeof graphQLError === "object" &&
        graphQLError !== null &&
        "message" in graphQLError &&
        graphQLError.message === MFA_REQUIRED_ERROR
    )
  );
};

const executeGraphQL = async <T>(query: string, variables: Record<string, unknown>, token?: string): Promise<T> => {
  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  const result = (await response.json()) as GraphQLResponse<T>;

  if (!response.ok || result.errors?.length || !result.data) {
    throw new Error(result.errors?.[0]?.message || "Authentication request failed");
  }

  return result.data;
};

const activateSession = async (
  surface: AuthSurface,
  token: string,
  email: string,
  method?: AuthMethod
): Promise<void> => {
  clearAuthData();
  await client.clearStore();
  storeAuthData({ token, status: LoginStatus.Registered, email, surface, ...(method ? { method } : {}) });
};

const loginForLearning = async (email: string, password: string): Promise<LearningAuthPayload> => {
  try {
    const data = await executeGraphQL<LearningLoginData>(LEARNING_LOGIN_MUTATION, { email, password });
    if (!data.learningLogin?.token) throw new Error("Learning login failed");
    return data.learningLogin;
  } catch {
    throw new LearningLoginRejectedError("Login failed");
  }
};

export const requestLearningPasswordReset = async (email: string): Promise<void> => {
  const data = await executeGraphQL<LearningRequestPasswordResetData>(LEARNING_REQUEST_PASSWORD_RESET_MUTATION, {
    email,
  });
  if (data.learningRequestPasswordReset !== true) throw new Error("Password reset request failed");
};

export const resetLearningPassword = async ({
  email,
  code,
  newPassword,
}: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> => {
  const data = await executeGraphQL<LearningResetPasswordData>(LEARNING_RESET_PASSWORD_MUTATION, {
    email,
    code,
    newPassword,
  });
  const token = data.learningResetPassword?.token;
  if (!token) throw new Error("Password reset failed");
  await activateSession("learning", token, email);
};

export const completeAdminMfa = async (input: CompleteAdminMfaInput): Promise<AdminMfaResponse> => {
  const data = await executeGraphQL<CompleteAdminMfaData>(COMPLETE_ADMIN_MFA, { input });
  if (!data.completeAdminMfa) throw new Error("Admin MFA completion failed");
  return data.completeAdminMfa;
};

export const finishAdminMfaLogin = async (email: string, token: string): Promise<void> => {
  await activateSession("admin", token, email);
};

const getAdminMfaSession = () => {
  const auth = getAuthData();
  if (!auth || auth.surface !== "admin") throw new Error("Not authenticated");
  return auth;
};

const executeAdminMfaGraphQL = async <T>(query: string, variables: Record<string, unknown>): Promise<T> => {
  try {
    return await executeGraphQL<T>(query, variables, getAdminMfaSession().token);
  } catch (error) {
    if (isMfaRequiredError(error)) void logout();
    throw error;
  }
};

export const getAdminMfaSettings = async (): Promise<AdminMfaSettings> => {
  const data = await executeAdminMfaGraphQL<AdminMfaSettingsData>(ADMIN_MFA_SETTINGS, {});
  if (!data.adminMfaSettings) throw new Error("Admin MFA settings unavailable");
  return data.adminMfaSettings;
};

export const beginAdminMfaEnrollment = async (currentPassword: string): Promise<AdminMfaResponse> => {
  const data = await executeAdminMfaGraphQL<BeginAdminMfaEnrollmentData>(BEGIN_ADMIN_MFA_ENROLLMENT, {
    currentPassword,
  });
  if (!data.beginAdminMfaEnrollment) throw new Error("Admin MFA enrollment failed");
  return data.beginAdminMfaEnrollment;
};

export const cancelAdminMfaEnrollment = async (): Promise<boolean> => {
  const data = await executeAdminMfaGraphQL<CancelAdminMfaEnrollmentData>(CANCEL_ADMIN_MFA_ENROLLMENT, {});
  if (data.cancelAdminMfaEnrollment === null || data.cancelAdminMfaEnrollment === undefined) {
    throw new Error("Admin MFA cancellation failed");
  }
  return data.cancelAdminMfaEnrollment;
};

export const disableAdminMfa = async (input: DisableAdminMfaInput): Promise<AdminMfaResponse> => {
  const data = await executeAdminMfaGraphQL<DisableAdminMfaData>(DISABLE_ADMIN_MFA, { input });
  if (!data.disableAdminMfa) throw new Error("Admin MFA disablement failed");
  return data.disableAdminMfa;
};

export const replaceAdminMfaSession = (accessToken: string, expectedEmail: string): void => {
  const auth = getAdminMfaSession();
  if (auth.email.toLocaleLowerCase() !== expectedEmail.toLocaleLowerCase()) {
    throw new Error("Admin MFA session account changed");
  }
  storeAuthData({ ...auth, token: accessToken });
};

let accessRequest: Promise<PortalAccess | null> | undefined;

// Concurrent checks share a single access load and report its failure once
// per load. A failure stays an error: a denial would be cached as a valid
// answer.
const readPortalAccess = (): Promise<PortalAccess | null> => {
  if (!accessRequest) {
    accessRequest = getPortalAccess()
      .catch(error => {
        if (!isMfaRequiredError(error)) captureException(error, "auth/access: get current user access");
        throw error;
      })
      .finally(() => {
        accessRequest = undefined;
      });
  }
  return accessRequest;
};

// A refresh boundary must finish an older load before asking for new roles;
// otherwise concurrent checks can reuse the response from before the change.
export const waitForPortalAccess = async (): Promise<void> => {
  await accessRequest?.catch(() => undefined);
};

// Binds the provider's redirect back to the browser tab that started it.
const COMPANY_SIGN_IN_STATE_KEY = "companySignIn.state";

export enum CompanySignInOutcome {
  None = "none",
  Authenticated = "authenticated",
  Failed = "failed",
  NoAccess = "noAccess",
  Cancelled = "cancelled",
}

export const startCompanySignIn = async (email?: string): Promise<void> => {
  const data = await executeGraphQL<BeginCompanySignInData>(BEGIN_COMPANY_SIGN_IN, {
    email: email?.trim() || null,
    // The provider sends the browser back to the root of this portal.
    redirectUri: `${window.location.origin}/`,
  });
  const start = data.beginCompanySignIn;
  if (!start) throw new Error("Company sign-in failed");
  sessionStorage.setItem(COMPANY_SIGN_IN_STATE_KEY, start.state);
  window.location.assign(start.authorizationUrl);
};

/** Whether the provider sent the browser back with a result to complete. */
export const hasCompanySignInCallback = (): boolean => {
  // The phone sign-in returns to its own path with the same query parameters.
  if (window.location.pathname === PHONE_SIGN_IN_PATH) return false;
  const parameters = new URLSearchParams(window.location.search);
  return parameters.has("state") && (parameters.has("code") || parameters.has("error"));
};

/** Finishes a company sign-in when the provider sent the browser back. */
export const completeCompanySignInFromUrl = async (): Promise<CompanySignInOutcome> => {
  if (!hasCompanySignInCallback()) return CompanySignInOutcome.None;

  const parameters = new URLSearchParams(window.location.search);
  const returnedState = parameters.get("state");

  const expectedState = sessionStorage.getItem(COMPANY_SIGN_IN_STATE_KEY);
  sessionStorage.removeItem(COMPANY_SIGN_IN_STATE_KEY);
  const query = window.location.search;
  // The code and state must not stay in the address bar or the history.
  window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.hash}`);

  if (!expectedState || returnedState !== expectedState) return CompanySignInOutcome.Failed;
  if (parameters.has("error")) {
    return parameters.get("error") === "access_denied" ? CompanySignInOutcome.Cancelled : CompanySignInOutcome.Failed;
  }

  const data = await executeGraphQL<CompleteCompanySignInData>(COMPLETE_COMPANY_SIGN_IN, { input: { query } });
  const result = data.completeCompanySignIn;
  if (result?.status === CompanySignInStatus.Authenticated && result.jwtToken && result.email) {
    await activateSession("admin", result.jwtToken, result.email, "company");
    return CompanySignInOutcome.Authenticated;
  }
  return result?.status === CompanySignInStatus.NoAccess ? CompanySignInOutcome.NoAccess : CompanySignInOutcome.Failed;
};

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const data = await executeGraphQL<{ loginUser?: LoginUserResponse | null }>(LOGIN_MUTATION, { email, password });
    const loginData = data.loginUser;

    if (loginData?.mfa) {
      return {
        loginStatus: loginData.loginStatus,
        jwtToken: null,
        mfa: loginData.mfa,
      };
    }

    if (loginData?.jwtToken) {
      await activateSession("admin", loginData.jwtToken, email);
      return {
        loginStatus: loginData.loginStatus,
        jwtToken: loginData.jwtToken,
        surface: "admin",
      };
    }

    if (loginData?.loginStatus === LoginStatus.Registering) {
      return { loginStatus: loginData.loginStatus, jwtToken: null };
    }

    if (loginData?.loginStatus === LoginStatus.ResettingPassword) {
      return { loginStatus: loginData.loginStatus, jwtToken: null };
    }

    if (loginData?.loginStatus === LoginStatus.Registered) {
      const learning = await loginForLearning(email, password);
      await activateSession("learning", learning.token, email);
      return {
        loginStatus: LoginStatus.Registered,
        jwtToken: learning.token,
        surface: "learning",
      };
    }

    throw new Error("Login failed");
  },

  logout: async () => {
    await logout();
  },

  changePassword: async ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) => {
    const auth = getAuthData();
    if (!auth?.email) throw new Error("Not authenticated");

    if (auth.surface === "learning") {
      const data = await executeGraphQL<LearningChangePasswordData>(
        LEARNING_CHANGE_PASSWORD_MUTATION,
        { currentPassword: oldPassword, newPassword },
        auth.token
      );
      const token = data.learningChangePassword?.token;
      if (!token) throw new Error("Password change failed");
      await activateSession("learning", token, auth.email);
      return;
    }

    try {
      const changed = await executeGraphQL<ChangeAdminPasswordData>(
        CHANGE_ADMIN_PASSWORD,
        { currentPassword: oldPassword, newPassword },
        auth.token
      );
      if (changed.changeAdminPassword !== true) throw new Error("Password change failed");
    } catch (error) {
      if (error instanceof Error && error.message === "invalid-password") {
        throw new Error("Old password is incorrect");
      }
      throw error;
    }
  },

  checkError: error => {
    if (error.status === 403) refreshPortalAccess();
    if (error.status === 401) {
      void logout();
      return Promise.reject();
    }
    if (isMfaRequiredError(error)) {
      void logout();
      return Promise.reject(error);
    }
    return Promise.resolve();
  },

  checkAuth: () => {
    if (getAuthData()) return Promise.resolve();
    // Redirect to login to finish the callback without the premature authentication notification.
    if (hasCompanySignInCallback()) return Promise.reject({ message: false });
    return Promise.reject();
  },

  getIdentity: async () => getCurrentUserIdentity(),

  // react-admin registers the <Resource> routes from this list. New checks use canAccess;
  // the analytics pages, StandortSelector and PractitionerShow still read the list.
  getPermissions: async () => {
    const access = await readPortalAccess();
    return access ? accessibleResources(access) : [];
  },

  canAccess: async ({ resource }) => {
    const access = await readPortalAccess();
    return access !== null && canAccessResource(access, resource);
  },
};
